import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import { inr, fmtDate } from "../../lib/format";
import { StatusBadge } from "../../components/StatusBadge";
import { Users, ArrowDownToLine, ArrowUpFromLine, Wallet, Clock, Lock } from "lucide-react";

const Stat = ({ label, value, icon: Icon, i, accent }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: i * 0.04 }}
    className="bg-card border border-border p-6 rounded-sm hover:-translate-y-px transition-transform"
    data-testid={`admin-stat-${label.toLowerCase().replace(/ /g, "-")}`}
  >
    <div className="flex items-center justify-between mb-4">
      <span className="overline">{label}</span>
      <Icon size={16} style={{ color: accent || "currentColor" }} className={accent ? "" : "text-muted-foreground"} />
    </div>
    <p className="text-3xl font-bold font-mono-num tracking-tighter">{value}</p>
  </motion.div>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/stats").then((r) => setD(r.data));
  }, []);

  return (
    <div>
      <p className="overline">Command Center</p>
      <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1 mb-8">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Total Users" value={d?.users_count ?? "—"} icon={Users} i={0} />
        <Stat label="Total Balance" value={inr(d?.total_balance)} icon={Wallet} i={1} accent="#059669" />
        <Stat label="Total Deposit" value={inr(d?.total_deposit)} icon={ArrowDownToLine} i={2} />
        <Stat label="Total Withdrawal" value={inr(d?.total_withdrawal)} icon={ArrowUpFromLine} i={3} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        <Stat label="Pending Deposits" value={d?.pending_deposits ?? "—"} icon={Clock} i={4} accent="#2563EB" />
        <Stat label="Pending Withdrawals" value={d?.pending_withdrawals ?? "—"} icon={Clock} i={5} accent="#D97706" />
        <Stat label="Amount On Hold" value={inr(d?.total_held)} icon={Lock} i={6} />
      </div>

      <h2 className="font-heading font-bold text-xl tracking-tight mb-4">Recent Activity</h2>
      <div className="border border-border rounded-sm overflow-x-auto bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left overline border-b border-border">
              <th className="px-5 py-3 font-semibold">User</th>
              <th className="px-5 py-3 font-semibold">Type</th>
              <th className="px-5 py-3 font-semibold">Amount</th>
              <th className="px-5 py-3 font-semibold hidden md:table-cell">Date</th>
              <th className="px-5 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {(d?.recent || []).map((t) => (
              <tr key={t.id} onClick={() => navigate(`/admin/users/${t.user_id}`)} className="border-b border-border/40 last:border-0 hover:bg-white/5 cursor-pointer">
                <td className="px-5 py-3">{t.user_name || t.user_email}</td>
                <td className="px-5 py-3 capitalize">{t.type}</td>
                <td className="px-5 py-3 font-mono-num font-medium">{inr(t.amount)}</td>
                <td className="px-5 py-3 text-xs text-muted-foreground hidden md:table-cell">{fmtDate(t.created_at)}</td>
                <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
